import tokens from "@/design/tokens";
import { Card, Label, MonoMeta, StateWord, Track, WaitingChip } from "@/design/primitives";
import { STATE_WORD } from "@/screens/breakage/states";
import { WATCH_STATES, WATCHES, buildTrack } from "@/data/breakageBoard.sample";

const Block = ({ title, children }) => (
  <Card as="section" className="p-6">
    <Label as="h2" className="mb-5 block">{title}</Label>
    {children}
  </Card>
);

const Rows = ({ children }) => <div className="divide-y divide-os-border">{children}</div>;
const Row = ({ name, children }) => (
  <div className="grid grid-cols-[200px_1fr] items-center gap-6 py-3">
    <p className="text-[13px] font-medium text-os-ink">{name}</p>
    <div className="min-w-0">{children}</div>
  </div>
);

const CanvasSpecimen = () => (
  <Block title="Canvas">
    <div className="os-canvas h-40 rounded-chip border border-os-border" />
    <MonoMeta as="p" className="mt-3">canvas #FFFFFF · ink dots at 0.05 opacity on a 22px grid</MonoMeta>
  </Block>
);

const Palette = () => (
  <Block title="Palette">
    <div className="grid grid-cols-2 gap-5 md:grid-cols-4 xl:grid-cols-7">
      {tokens.swatches.map(({ token, role }) => (
        <div key={token} data-testid={`dna-swatch-${token}`}>
          <div className="h-14 rounded-chip border border-os-border" style={{ background: tokens.colors[token] }} />
          <p className="mt-2 text-[13px] font-medium text-os-ink">{token}</p>
          <MonoMeta as="p" className="mt-1">{tokens.colors[token]}</MonoMeta>
          <p className="mt-1 text-[12px] leading-snug text-os-ink-2">{role}</p>
        </div>
      ))}
    </div>
  </Block>
);

const CardSpecimen = () => (
  <Block title="Card">
    <div className="grid gap-6 md:grid-cols-2">
      <Card className="p-6">
        <p className="text-[15px] font-semibold tracking-[-0.01em]">Card</p>
        <div className="mt-3 space-y-2">
          {["fill #FFFFFF → #FCFCFB, vertical", "hairline #E5E7EB", `shadow ${tokens.effects.cardShadow}`, "engraved 1px light line on the top edge", `radius ${tokens.radius.card}`].map((t) => (
            <MonoMeta as="p" key={t} className="leading-snug">{t}</MonoMeta>
          ))}
        </div>
      </Card>
      <Card quiet className="p-6">
        <p className="text-[15px] font-semibold tracking-[-0.01em]">Quiet card</p>
        <MonoMeta as="p" className="mt-3 leading-snug">hairline only — no shadow, no fill. Rests on the canvas.</MonoMeta>
      </Card>
    </div>
    <MonoMeta as="p" className="mt-4">radius · card {tokens.radius.card} · chip {tokens.radius.chip} · track segment {tokens.radius.segment}</MonoMeta>
  </Block>
);

const TypeScale = () => (
  <Block title="Type — Inter + IBM Plex Mono">
    <Rows>
      {tokens.typeScale.map((t) => (
        <Row key={t.name} name={t.name}>
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <p
              className={t.family === "mono" ? "font-mono text-os-ink" : "font-inter text-os-ink"}
              style={{ fontSize: t.size, fontWeight: t.weight, letterSpacing: t.tracking, textTransform: t.case === "UPPERCASE" ? "uppercase" : "none" }}
            >
              {t.sample}
            </p>
            <MonoMeta>{t.family === "mono" ? "Plex Mono" : "Inter"} · {t.size} · {t.weight} · {t.tracking} · {t.case}</MonoMeta>
          </div>
        </Row>
      ))}
    </Rows>
  </Block>
);

const StatusSpecimens = () => (
  <Block title="State words & waiting chip">
    <div className="flex flex-wrap gap-x-8 gap-y-3">
      {WATCH_STATES.map((s) => (
        <StateWord key={s} tone={STATE_WORD[s].tone}>{STATE_WORD[s].word}</StateWord>
      ))}
    </div>
    <WaitingChip className="mt-6 max-w-md">Waiting on you: approve the refund for Dory's cancelled booking.</WaitingChip>
    <MonoMeta as="p" className="mt-3">gold #FFC838 as a 0.22 tint · bronze keyline with a stronger bottom edge · graphite text — the only place gold appears</MonoMeta>
  </Block>
);

const SPECIMEN = { healthy: "seo-audit", broken: "inbox-scan", alert: "lead-router", waiting: "payment-reconciliation", stale: "seo-audit", empty: "city-page-build" };
const runsFor = (state) => buildTrack(WATCHES.find((w) => w.slug === SPECIMEN[state]).pastRuns, state);

const TrackSpecimens = () => (
  <Block title="Segmented track">
    <Rows>
      {WATCH_STATES.map((s) => (
        <Row key={s} name={STATE_WORD[s].word}>
          <div className={s === "stale" ? "opacity-40" : undefined}>
            <Track runs={runsFor(s)} emptyLabel="no runs yet" />
          </div>
        </Row>
      ))}
    </Rows>
    <div className="mt-4 space-y-1 text-[12px] leading-relaxed text-os-ink-2">
      <p>16 equal segments, 6px tall, 3px gaps, 2px radius. Oldest left, newest right.</p>
      <p>Ink = clean. Past problems use their signal at 0.35 opacity. The newest segment is the current state at full strength. Light gray = didn't run.</p>
      <p>Stale dims number, track and count together. Quiet shows sixteen gray segments and “no runs yet”.</p>
      <p className="font-mono text-[11px] text-os-ink-3">Count: “{"{clean}"} / 16 clean” · “{"{n}"} failed” · “{"{n}"} flagged” · “{"{n}"} missed” — each extra part only when above zero.</p>
    </div>
  </Block>
);

const ListBlock = ({ title, items }) => (
  <Block title={title}>
    <Rows>
      {items.map((i) => (
        <Row key={i.name} name={i.name}>
          <p className="text-[13px] text-os-ink-2">{i.rule}</p>
        </Row>
      ))}
    </Rows>
  </Block>
);

const Forbidden = () => (
  <Block title="Forbidden">
    <div className="grid gap-6 md:grid-cols-2">
      {Object.entries(tokens.forbidden).map(([kind, items]) => (
        <div key={kind}>
          <MonoMeta as="p" className="mb-2 uppercase">{kind}</MonoMeta>
          <ul className="space-y-1.5 text-[13px] text-os-ink-2">
            {items.map((t) => <li key={t}>— {t}</li>)}
          </ul>
        </div>
      ))}
    </div>
  </Block>
);

export const DnaSheet = () => (
  <div data-testid="dna-sheet" className="flex max-w-[1400px] flex-col gap-6 font-inter">
    <CanvasSpecimen />
    <Palette />
    <CardSpecimen />
    <TypeScale />
    <StatusSpecimens />
    <TrackSpecimens />
    <ListBlock title="Motion" items={tokens.motion} />
    <Forbidden />
  </div>
);
